'use client';
import IconChevronLeft from '@mnd-frontend/icons/components/ChevronLeft';
import IconChevronRight from '@mnd-frontend/icons/components/ChevronRight';
import {
  GREY_300,
  GREY_400,
  GREY_1000,
  NEUTRAL_WHITE,
  TEXT_FONT,
} from '@mnd-frontend/ui/shared/tokens';
import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Text } from '../Text';
import PaginationInput from './components/StyledInput';

type PaginationProps = {
  currentPage: number;
  totalPages: number;

  /* Will execute on page change with page as only param. */
  onChangePage: (page: number) => void;
};

const PaginationButton = styled.button`
  border: 0;
  padding: 0;
  background: none;
  border: 1px solid ${GREY_400};
  height: ${props => props.theme.sizingButtonHeight};
  width: ${props => props.theme.sizingButtonWidthIconOnly};
  color: ${props => props.theme.colorTextButtonTextTextButtonOnLightDefault};
  border-radius: ${props => props.theme.radiusButton};
  &:disabled {
    color: ${props => props.theme.colorTextButtonTextTextButtonOnLightDisabled};
  }
  &:hover:not(:disabled) {
    background-color: ${GREY_300};
  }
`;

const Pagination = styled(
  ({
    className,
    onChangePage,
    currentPage,
    totalPages,
  }: PaginationProps & { className?: string }) => {
    const disabled: boolean = totalPages === 1;
    const inputField: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(
      document.createElement('input'),
    );
    const [t] = useTranslation();
    const [inputValue, setInputValue] = useState<string>('');
    const [inputVisible, setInputVisible] = useState<boolean>(false);

    const hideInput = useCallback(() => {
      setInputVisible(false);
    }, []);

    const handleInputChange = useCallback((event: any) => {
      setInputValue(Number.parseInt(event.target.value, 10).toString());
    }, []);

    const handleChange = useCallback(
      (value: any) => {
        const page = Math.max(1, Math.min(value, totalPages));
        if (!Number.isNaN(page) && page !== currentPage) {
          onChangePage(page);
        }
      },
      [currentPage, onChangePage, totalPages],
    );

    const handleBlur = useCallback(() => {
      hideInput();
    }, [hideInput]);

    const handleNext = useCallback(() => {
      handleChange(currentPage + 1);
    }, [handleChange, currentPage]);

    const handlePrevious = useCallback(() => {
      handleChange(currentPage - 1);
    }, [handleChange, currentPage]);

    const handleShowInput = useCallback(() => {
      !disabled && setInputVisible(true);
    }, [disabled]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
          case 'Enter':
            hideInput();
            handleChange(inputValue);
            break;
          case 'Backspace':
            break;
          case 'Escape':
            hideInput();
            break;
          default:
            if (/^[^0-9]$/i.test(event.key)) {
              hideInput();
            }
        }
      },
      [hideInput, handleChange, inputValue],
    );

    // Keep input input value in sync with currentPage
    useEffect(() => {
      setInputValue(String(currentPage));
    }, [currentPage]);

    // Handle focus
    useEffect(() => {
      if (inputVisible && inputField?.current) {
        inputField.current.focus();
      }
    }, [inputVisible]);

    // Clear ui input field
    useEffect(() => {
      if (inputVisible && inputField?.current) {
        inputField.current.value = '';
      }
    }, [inputVisible]);

    return (
      <nav aria-label={t('components_pagination_aria-label')} className={className}>
        <PaginationButton
          disabled={disabled || currentPage === 1}
          aria-label={t('components_pagination_previous-page')}
          onClick={handlePrevious}
          onMouseDown={hideInput}
        >
          <IconChevronLeft />
        </PaginationButton>
        {/** biome-ignore lint/a11y/noStaticElementInteractions: ? */}
        <div onClick={handleShowInput}>
          <Text as="p" variant="body" hidden={inputVisible}>
            {t('components_pagination_label', { currentPage, totalPages })}
          </Text>
          <PaginationInput
            ref={inputField}
            hidden={!inputVisible}
            type="number"
            pattern={'\\d*'}
            value={inputValue}
            placeholder={String(currentPage)}
            onBlur={handleBlur}
            onChange={handleInputChange}
            aria-label={t('components_pagination_quick-navigation_label')}
            onKeyDown={handleKeyDown}
          />
        </div>
        <PaginationButton
          disabled={disabled || currentPage === totalPages}
          aria-label={t('components_pagination_next-page')}
          onClick={handleNext}
          onMouseDown={hideInput}
        >
          <IconChevronRight />
        </PaginationButton>
      </nav>
    );
  },
)<PaginationProps>`
  p,
  input {
    color: ${GREY_1000};
    font-family: ${TEXT_FONT};
    font-size: 1rem;
    font-weight: normal;
    line-height: 1.25;
    text-transform: uppercase;
    margin: 0;
  }

  display: inline-flex;
  position: relative;
  height: 2.75rem;
  align-items: center;
  text-align: center;
  border-radius: ${props => props.theme.radiusButton};
  border: 0;
  background: ${NEUTRAL_WHITE};

  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 7.438rem;
    border-top: 1px solid ${GREY_400};
    border-bottom: 1px solid ${GREY_400};
  }

  & > *:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  & > *:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

export default Pagination;
